import { useTranslation } from 'react-i18next'
import { useQueries } from '@tanstack/react-query'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import Card from './Card'
import LoadingIndicator from './LoadingIndicator'

export type VersionService = {
  testId: string
  queryKey: string
  labelKey: string
  queryFn: () => Promise<{ version: string } | null>
}

type VersionProps = {
  softwareVersion: string
  frontendVersion: string
  services: VersionService[]
  testIdInit?: string
}

export default function Version({
  softwareVersion,
  frontendVersion,
  services,
  testIdInit = 'version'
}: Readonly<VersionProps>) {
  const { t } = useTranslation('common')

  const results = useQueries({
    queries: services.map((service) => ({
      queryKey: [service.queryKey],
      queryFn: async () => (await service.queryFn()) ?? null,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }))
  })

  return (
    <div data-testid={testIdInit}>
      <Popover>
        <PopoverButton>
          <span data-testid={`${testIdInit}-software-label`}>
            {t('version.gesamt')}
          </span>
          <span data-testid={`${testIdInit}-software-colon`}>: </span>
          <span data-testid={`${testIdInit}-software-value`}>
            {softwareVersion}
          </span>
        </PopoverButton>
        <PopoverPanel transition anchor="top" focus>
          <Card
            role="region"
            aria-label={t('version.gesamt')}
            tabIndex={-1}
            className="flex shadow-lg rounded-md mb-1.5 !whitespace-nowrap dark:text-cool-gray-200"
            data-testid={`${testIdInit}-software-card`}
          >
            <dl
              className="px-1.5 py-1 flex flex-col gap-0.5"
              data-testid={`${testIdInit}-software-wrapper`}
            >
              <VersionRow
                testIdInit={testIdInit}
                testId="frontend"
                label={t('version.components.frontend')}
                value={frontendVersion}
              />
              {services.map((service, index) => (
                <VersionRow
                  key={service.testId}
                  testIdInit={testIdInit}
                  testId={service.testId}
                  label={t(service.labelKey)}
                  value={results[index].data?.version ?? ''}
                  isPending={results[index].isPending}
                />
              ))}
            </dl>
          </Card>
        </PopoverPanel>
      </Popover>
    </div>
  )
}

type VersionRowProps = {
  testIdInit: string
  testId: string
  label: string
  value: string
  isPending?: boolean
}

function VersionRow({
  testIdInit,
  testId,
  label,
  value,
  isPending = false
}: Readonly<VersionRowProps>) {
  return (
    <div className="flex gap-1">
      <dt
        data-testid={`${testIdInit}-${testId}-label`}
        className="inline-block w-7 text-right"
      >
        {label}:
      </dt>
      <dd data-testid={`${testIdInit}-${testId}-backend-value`}>
        {isPending ? (
          <LoadingIndicator
            size="xs"
            className="inline mx-0.5"
            data-testid={`${testIdInit}-${testId}-loading`}
          />
        ) : (
          value
        )}
      </dd>
    </div>
  )
}
